import { i18n } from 'src/boot/i18n'
import { DASHBOARD_PENDING, ORDER_MODE, SELECTION_LIMIT, USER_ACCESS } from '@/constants/analytics.js'

const FIELD_LABEL_KEYS = {
  workspace_id: 'analytics.dashboard_field_workspace_id',
  report_id: 'analytics.dashboard_field_report_id',
  dataset_id: 'analytics.dashboard_field_dataset_id',
  rls_role: 'analytics.dashboard_field_rls_role'
}

// Cada lugar que avisa de un tablero incompleto tiene su frase, en singular y plural.
const MISSING_FIELDS_TEMPLATES = {
  table: 'analytics.dashboard_incomplete_field',
  banner: 'analytics.dashboard_incomplete_banner_detail',
  usage: 'analytics.dashboard_usage_empty_incomplete'
}

function fieldLabel (field) {
  return FIELD_LABEL_KEYS[field] ? i18n.global.t(FIELD_LABEL_KEYS[field]) : field
}

// "A, B y C" en español, "A, B, and C" en inglés.
function joinLabels (labels) {
  const locale = i18n.global.locale?.value || 'es'

  return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(labels)
}

/**
 * @param {string[]} missingFields campos que reporta el backend en `missing_fields`
 * @param {'table'|'banner'|'usage'} variant dónde se muestra el aviso
 */
export function missingFieldsText (missingFields = [], variant = 'table') {
  if (missingFields.length === 0) {
    return ''
  }

  const template = MISSING_FIELDS_TEMPLATES[variant]
  const labels = missingFields.map(fieldLabel)

  if (labels.length === 1) {
    return i18n.global.t(`${template}_singular`, { field: labels[0] })
  }

  return i18n.global.t(`${template}_plural`, { fields: joinLabels(labels) })
}

// Mismo patrón de GUID que valida el backend al guardar.
const GUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}'
const GUID_PATTERN = new RegExp(`^${GUID}$`)
const REPORT_PATH = new RegExp(`groups/(${GUID})/reports/(${GUID})`)
const DATASET_PATH = new RegExp(`groups/(${GUID})/datasets/(${GUID})`)
const REPORT_ID_PARAM = new RegExp(`[?&]reportId=(${GUID})`, 'i')
const GROUP_ID_PARAM = new RegExp(`[?&]groupId=(${GUID})`, 'i')

export function isGuid (value) {
  return typeof value === 'string' && GUID_PATTERN.test(value)
}

/**
 * Extrae los IDs de una URL de Power BI y devuelve solo los que encuentra:
 * - reporte: `.../groups/{workspace}/reports/{report}/...`
 * - dataset: `.../groups/{workspace}/datasets/{dataset}/...`
 * - enlace de insertar: `.../reportEmbed?reportId={report}&groupId={workspace}`
 * Devuelve null si no reconoce ninguno. Las URLs de apps (`groups/me/apps/{app}/reports/{report}`) no se reconocen a propósito:
 * no traen el workspace y su id de reporte es el de la app, no el del reporte original.
 */
export function parsePowerBiUrl (url) {
  if (typeof url !== 'string') return null

  const report = REPORT_PATH.exec(url)
  if (report) return { workspaceId: report[1], reportId: report[2] }

  const dataset = DATASET_PATH.exec(url)
  if (dataset) return { workspaceId: dataset[1], datasetId: dataset[2] }

  const reportId = REPORT_ID_PARAM.exec(url)
  if (!reportId) return null

  const groupId = GROUP_ID_PARAM.exec(url)

  return groupId ? { workspaceId: groupId[1], reportId: reportId[1] } : { reportId: reportId[1] }
}

/**
 * Desactivar pide confirmación solo si el tablero se usa en alguna organización.
 * @param {object|null} dashboard versión guardada del tablero (null en el alta)
 * @param {boolean} isActive estado que se va a guardar
 */
export function needsDeactivateConfirm (dashboard, isActive) {
  return Boolean(dashboard?.isActive) && !isActive && isDashboardInUse(dashboard)
}

// En uso: habilitado en alguna organización o con usuarios que ya tienen acceso.
function isDashboardInUse (dashboard) {
  return dashboard.organizationsCount > 0 || dashboard.usersCount > 0
}

// Campos del formulario que deciden qué reporte de Power BI se muestra.
const CONNECTION_FIELDS = ['workspaceId', 'reportId', 'datasetId', 'rlsRole']

// Como viaja al backend: sin espacios en los extremos y el vacío igual al null.
const connectionValue = (value) => (value || '').trim()

/**
 * Cambiar la conexión de un tablero en uso pide confirmación: todas sus organizaciones pasan a ver
 * otro reporte. Si el tablero se está desactivando ya se confirma eso, y uno inactivo no lo ve nadie.
 * @param {object|null} dashboard versión guardada del tablero (null en el alta)
 * @param {object} form valores del formulario que se van a guardar
 */
export function needsConnectionChangeConfirm (dashboard, form) {
  return Boolean(dashboard?.isActive) && Boolean(form.isActive) && isDashboardInUse(dashboard) &&
    CONNECTION_FIELDS.some(field => connectionValue(form[field]) !== connectionValue(dashboard[field]))
}

/**
 * El formulario de tablero tiene cambios sin guardar. Son cadenas y booleanos, así que basta comparar campo a campo.
 * @param {object} form valores actuales
 * @param {object} baseline valores con los que se cargó o se guardó por última vez
 */
export function isDashboardFormDirty (form, baseline) {
  return Object.keys(baseline).some(key => form[key] !== baseline[key])
}

// Botones de los confirms del módulo: cancelar discreto y la acción en rojo.
function confirmButtons (okLabel, cancelLabel = i18n.global.t('common.cancel')) {
  return {
    cancel: { label: cancelLabel, color: 'binnacle-grey', flat: true, noCaps: true },
    ok: { label: okLabel, color: 'binnacle-red', unelevated: true, rounded: true, noCaps: true }
  }
}

// Opciones del confirm nativo de Quasar ($q.dialog) para desactivar un tablero en uso.
export function deactivateDialogOptions (dashboard) {
  const { t } = i18n.global

  return {
    title: t('analytics.dashboard_deactivate_title'),
    message: t('analytics.dashboard_deactivate_message', {
      name: dashboard.name,
      organizations: t('analytics.dashboard_usage_organizations_count', dashboard.organizationsCount),
      users: t('analytics.dashboard_deactivate_users_count', dashboard.usersCount)
    }),
    ...confirmButtons(t('analytics.action_deactivate'))
  }
}

// Opciones del confirm nativo de Quasar ($q.dialog) para cambiar la conexión de un tablero en uso.
export function connectionChangeDialogOptions (dashboard) {
  const { t } = i18n.global

  return {
    title: t('analytics.dashboard_connection_title'),
    message: t('analytics.dashboard_connection_message', {
      name: dashboard.name,
      organizations: t('analytics.dashboard_usage_organizations_count', dashboard.organizationsCount),
      users: t('analytics.dashboard_deactivate_users_count', dashboard.usersCount)
    }),
    ...confirmButtons(t('analytics.action_change_connection'))
  }
}

const ORDER_CUSTOM = ORDER_MODE.custom

/**
 * Cambios pendientes de la pestaña Tableros de una organización.
 * @param {{ orderMode: string, dashboards: { id: number }[] }} saved ficha guardada
 * @param {number[]} draftIds conjunto final, en el orden en que se ve
 * @param {'catalog'|'custom'} draftOrderMode
 */
export function organizationDashboardsDiff (saved, draftIds, draftOrderMode) {
  const savedIds = saved.dashboards.map(dashboard => dashboard.id)
  const added = draftIds.filter(id => !savedIds.includes(id))
  const removed = savedIds.filter(id => !draftIds.includes(id))

  // En orden del catálogo la posición la decide el catálogo; en personalizado, cuenta el orden
  // de los que se conservan (los agregados ya están en `added`).
  const keptDraft = draftIds.filter(id => savedIds.includes(id))
  const keptSaved = savedIds.filter(id => draftIds.includes(id))
  const orderChanged = draftOrderMode !== saved.orderMode ||
    (draftOrderMode === ORDER_CUSTOM && keptDraft.some((id, index) => id !== keptSaved[index]))

  return { added, removed, orderChanged, hasChanges: added.length > 0 || removed.length > 0 || orderChanged }
}

// "1 tablero agregado · 2 tableros quitados · Orden modificado", solo con las partes que aplican.
export function pendingChangesText ({ added, removed, orderChanged }) {
  const { t } = i18n.global
  const parts = []

  if (added.length > 0) parts.push(t('analytics.pending_added_count', added.length))
  if (removed.length > 0) parts.push(t('analytics.pending_removed_count', removed.length))
  if (orderChanged) parts.push(t('analytics.dashboard_reorder_changed'))

  return parts.join(' · ')
}

/**
 * Tableros que se pueden ofrecer a la organización: activos, sin habilitar y en el orden del
 * catálogo. Los incompletos se muestran, pero no se pueden agregar.
 */
export function availableDashboards (catalog, enabledIds) {
  const available = catalog
    .filter(dashboard => dashboard.isActive && !enabledIds.includes(dashboard.id))
    .map(dashboard => ({ ...dashboard, canAdd: dashboard.isComplete }))

  return {
    general: available.filter(dashboard => dashboard.isGeneral),
    specific: available.filter(dashboard => !dashboard.isGeneral)
  }
}

// Los ids en el orden del catálogo; los que el catálogo no trae quedan al final.
export function catalogOrder (catalog, ids) {
  const positions = new Map(catalog.map((dashboard, index) => [dashboard.id, index]))
  const position = (id) => (positions.has(id) ? positions.get(id) : catalog.length)

  return [...ids].sort((a, b) => position(a) - position(b))
}

// Los tableros generales (kit general), en el orden del catálogo: los únicos que se reordenan, activos o no.
export function generalDashboards (catalog) {
  return catalog.filter(dashboard => dashboard.isGeneral)
}

// Los lugares (desde 1) que ocupan los generales en el catálogo completo, para numerar el modo reordenar.
export function generalPositions (catalog) {
  return catalog.flatMap((dashboard, index) => (dashboard.isGeneral ? [index + 1] : []))
}

/**
 * El catálogo completo (el backend lo exige así) con los generales en su orden nuevo: se reparten entre
 * los lugares que ya ocupaban y los específicos se quedan donde estaban.
 * @param {{ id: number, isGeneral: boolean }[]} catalog
 * @param {number[]} orderedIds los ids de los generales, ya reordenados
 * @returns {number[]} los ids de todo el catálogo
 */
export function mergeGeneralOrder (catalog, orderedIds) {
  const pending = [...orderedIds]

  return catalog.map(dashboard => (dashboard.isGeneral ? pending.shift() : dashboard.id))
}

// El orden personalizado que se tenía antes de pasar al del catálogo, para volver a él sin perderlo.
// Los que se agregaron o quitaron mientras tanto se respetan: los nuevos van al final.
export function restoreCustomOrder (ids, customIds) {
  const kept = customIds.filter(id => ids.includes(id))

  return [...kept, ...ids.filter(id => !kept.includes(id))]
}

/**
 * Filas de "Habilitados" mientras se edita, en el orden en que se ven. Las guardadas traen sus
 * conteos; las que se agregan, los datos del catálogo. Las marcadas para quitar conservan su lugar.
 * @param {{ dashboards: object[] }} saved ficha guardada
 * @param {object[]} catalog
 * @param {number[]} workingIds lista de trabajo, incluidas las marcadas para quitar
 * @param {number[]} removedIds
 */
export function organizationDashboardRows (saved, catalog, workingIds, removedIds) {
  const savedById = new Map(saved.dashboards.map(dashboard => [dashboard.id, dashboard]))
  const catalogById = new Map(catalog.map(dashboard => [dashboard.id, dashboard]))

  return workingIds.map((id, index) => {
    const isSaved = savedById.has(id)
    let pending = null

    if (!isSaved) {
      pending = DASHBOARD_PENDING.add
    } else if (removedIds.includes(id)) {
      pending = DASHBOARD_PENDING.remove
    }

    return { ...(isSaved ? savedById.get(id) : catalogById.get(id)), position: index + 1, pending }
  })
}

// En personalizado los agregados van al final; en orden del catálogo, a su lugar en el catálogo.
export function withAddedDashboards (catalog, workingIds, ids, orderMode) {
  const next = [...workingIds, ...ids.filter(id => !workingIds.includes(id))]

  return orderMode === ORDER_CUSTOM ? next : catalogOrder(catalog, next)
}

// En el alta se ofrece de entrada el kit general que sí se puede habilitar.
export function enableDialogSelection (catalog) {
  return catalog
    .filter(dashboard => dashboard.isGeneral && dashboard.isActive && dashboard.isComplete)
    .map(dashboard => dashboard.id)
}

const normalizeText = (text) => (text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

// Búsqueda sin distinguir mayúsculas ni acentos; sin búsqueda, todo coincide.
export function matchesSearch (text, search) {
  return !search || normalizeText(text).includes(normalizeText(search))
}

export function areAllSelected (selectedIds, ids) {
  return ids.length > 0 && ids.every(id => selectedIds.includes(id))
}

// Si ya están todos, los desmarca; si no, marca los que faltan. Los demás no se tocan.
export function toggleAllSelected (selectedIds, ids) {
  return areAllSelected(selectedIds, ids)
    ? selectedIds.filter(id => !ids.includes(id))
    : [...selectedIds, ...ids.filter(id => !selectedIds.includes(id))]
}

// Guardar la Identidad RLS exige un valor nuevo, distinto del actual, y confirmar a qué organización es.
export function canSaveRlsIdentity (current, next, confirmed) {
  const value = typeof next === 'string' ? next.trim() : ''

  return confirmed && value !== '' && value !== current
}

export function pauseDialogOptions (organization) {
  const { t } = i18n.global

  return {
    title: t('analytics.pause_title', { organization: organization.name }),
    message: t('analytics.pause_message', { count: organization.usersCount }, organization.usersCount),
    ...confirmButtons(t('analytics.action_pause'))
  }
}

/**
 * Confirm de guardar cuando se quitan tableros que tienen usuarios con acceso.
 * @param {{ name: string }} organization
 * @param {{ name: string, usersCount: number }[]} removed
 */
export function removeDashboardsDialogOptions (organization, removed) {
  const { t } = i18n.global
  const usersCount = removed.reduce((total, dashboard) => total + dashboard.usersCount, 0)
  const params = {
    organization: organization.name,
    users: t('analytics.dashboard_deactivate_users_count', usersCount)
  }
  const message = removed.length === 1
    ? t('analytics.remove_message_singular', { ...params, dashboard: removed[0].name })
    : t('analytics.remove_message_plural', { ...params, dashboards: joinLabels(removed.map(dashboard => dashboard.name)) })

  return {
    title: t('analytics.remove_title', removed.length),
    message,
    ...confirmButtons(t('analytics.action_remove'))
  }
}

/**
 * Confirm de salir con cambios sin guardar.
 * @param {string} [messageKey] clave del texto; el de la ficha de organización es el predeterminado
 */
export function discardChangesDialogOptions (messageKey = 'analytics.discard_message') {
  const { t } = i18n.global

  return {
    title: t('analytics.discard_title'),
    message: t(messageKey),
    ...confirmButtons(t('analytics.action_discard'), t('analytics.discard_keep'))
  }
}

/**
 * Buscar con "Con acceso" no encontraría a quien aún no tiene ninguno: el filtro pasa a "Todos"
 * y `widened` avisa para mostrarlo. "Sin acceso" y "Todos" se respetan.
 * @returns {{ access: string, widened: boolean }}
 */
export function accessFilterForSearch (access, search) {
  const hasSearch = typeof search === 'string' && search.trim() !== ''

  return hasSearch && access === USER_ACCESS.with
    ? { access: USER_ACCESS.all, widened: true }
    : { access, widened: false }
}

// Qué tanto de las columnas (los tableros de la organización) tiene el usuario; los demás ids no cuentan.
export function rowAccessState (userDashboardIds, columnIds) {
  const owned = columnIds.filter(id => userDashboardIds.includes(id)).length

  if (owned === 0) {
    return 'none'
  }

  return owned === columnIds.length ? 'all' : 'some'
}

/**
 * Qué pide la casilla "Todos" de una fila: con todas las columnas, quitarlas; si no, dar las que faltan.
 * A un usuario suspendido solo se le puede quitar lo que tiene.
 * @returns {{ hasAccess: boolean, dashboardIds: number[] }}
 */
export function rowToggleAllRequest (userDashboardIds, columnIds, { suspended = false } = {}) {
  if (suspended) {
    return { hasAccess: false, dashboardIds: columnIds.filter(id => userDashboardIds.includes(id)) }
  }

  if (rowAccessState(userDashboardIds, columnIds) === 'all') {
    return { hasAccess: false, dashboardIds: [...columnIds] }
  }

  return { hasAccess: true, dashboardIds: columnIds.filter(id => !userDashboardIds.includes(id)) }
}

/**
 * Quienes pueden recibir acceso: los suspendidos se omiten y se cuentan.
 * @param {{ isSuspended: boolean }[]} users
 * @returns {{ users: object[], skippedCount: number }}
 */
export function grantableUsers (users) {
  const eligible = users.filter(user => !user.isSuspended)

  return { users: eligible, skippedCount: users.length - eligible.length }
}

/**
 * Aplica a la selección general lo que se marcó y desmarcó en la página visible: lo de otras páginas
 * o búsquedas no se toca. Lo que ya no cabe en el tope no se agrega y se cuenta.
 * @param {{ id: number }[]} selection
 * @param {{ id: number }[]} pageRows las filas de la página visible
 * @param {{ id: number }[]} pageSelected las filas marcadas en esa página
 * @returns {{ selection: object[], rejectedCount: number }}
 */
export function mergePageSelection (selection, pageRows, pageSelected, limit = SELECTION_LIMIT) {
  const pageIds = new Set(pageRows.map(row => row.id))
  const checkedIds = new Set(pageSelected.map(row => row.id))
  const kept = selection.filter(user => !pageIds.has(user.id) || checkedIds.has(user.id))
  const keptIds = new Set(kept.map(user => user.id))
  const added = pageSelected.filter(row => !keptIds.has(row.id))
  const room = Math.max(limit - kept.length, 0)

  return { selection: [...kept, ...added.slice(0, room)], rejectedCount: Math.max(added.length - room, 0) }
}

// Las filas de la página visible que están seleccionadas.
export function selectedOnPage (selection, pageRows) {
  const selectedIds = new Set(selection.map(user => user.id))

  return pageRows.filter(row => selectedIds.has(row.id))
}

/**
 * "María Campos, Jorge Salcido y Sofía Reyes"; con más de `max` nombres, "… y 2 más".
 * @param {string[]} names
 */
export function peopleSummary (names, max = 3) {
  const shown = names.slice(0, max)
  const rest = names.length - shown.length

  return joinLabels(rest > 0 ? [...shown, i18n.global.t('analytics.people_more', { count: rest })] : shown)
}

const USER_ACCESS_DIALOG = {
  grantAll: { title: 'analytics.grant_all_title', message: 'analytics.grant_all_message' },
  grantOne: { title: 'analytics.grant_one_title', message: 'analytics.grant_one_message' },
  revokeAll: { title: 'analytics.revoke_all_title', message: 'analytics.revoke_all_message' },
  revokeOne: { title: 'analytics.revoke_one_title', message: 'analytics.revoke_one_message' }
}

/**
 * Confirm de dar o quitar acceso a los usuarios seleccionados, con el impacto en el texto.
 * @param {object} options
 * @param {{ name: string }} options.organization
 * @param {{ fullName: string }[]} options.users los usuarios seleccionados
 * @param {{ name: string }|null} options.dashboard el tablero elegido; null para todos
 * @param {number} options.dashboardsCount cuántos tableros habilitados tiene la organización
 * @param {boolean} options.hasAccess true para dar, false para quitar
 * @param {number} [options.skippedCount] suspendidos de la selección que no reciben acceso (solo al dar)
 */
export function userAccessDialogOptions ({ organization, users, dashboard, dashboardsCount, hasAccess, skippedCount = 0 }) {
  const { t } = i18n.global
  const keys = USER_ACCESS_DIALOG[`${hasAccess ? 'grant' : 'revoke'}${dashboard ? 'One' : 'All'}`]
  const params = {
    organization: organization.name,
    dashboard: dashboard?.name,
    count: users.length,
    people: peopleSummary(users.map(user => user.fullName))
  }
  const skipped = hasAccess && skippedCount > 0
    ? ` ${t('analytics.grant_skipped_suspended', { count: skippedCount }, skippedCount)}`
    : ''

  return {
    title: t(keys.title, { ...params, count: dashboardsCount }),
    message: t(keys.message, params, users.length) + skipped,
    ...confirmButtons(t(hasAccess ? 'analytics.action_grant' : 'analytics.action_revoke'))
  }
}

/**
 * Aviso tras una acción masiva: cuántos usuarios cambiaron o, si ninguno, que no hubo cambios.
 * @param {{ hasAccess: boolean, affectedUsersCount: number }} result
 */
export function userAccessResultMessage ({ hasAccess, affectedUsersCount }) {
  const { t } = i18n.global

  if (affectedUsersCount === 0) {
    return t(hasAccess ? 'analytics.notify_access_unchanged_grant' : 'analytics.notify_access_unchanged_revoke')
  }

  const key = hasAccess ? 'analytics.notify_access_granted' : 'analytics.notify_access_revoked'

  return t(key, { count: affectedUsersCount }, affectedUsersCount)
}

/**
 * Aviso mientras se guarda el acceso de una casilla, una fila o una acción masiva.
 * @param {{ hasAccess: boolean, count: number }} options count: cuántos tableros se tocan
 */
export function userAccessProgressMessage ({ hasAccess, count }) {
  const key = hasAccess ? 'analytics.notify_access_granting' : 'analytics.notify_access_revoking'

  return i18n.global.t(key, { count }, count)
}

/**
 * Aviso al terminar de guardar una casilla o una fila: que se asignó o quitó, o que no hubo cambios.
 * @param {{ hasAccess: boolean, count: number, affectedUsersCount: number }} options
 */
export function userAccessSavedMessage ({ hasAccess, count, affectedUsersCount }) {
  if (affectedUsersCount === 0) {
    return userAccessResultMessage({ hasAccess, affectedUsersCount })
  }

  const key = hasAccess ? 'analytics.notify_access_saved_grant' : 'analytics.notify_access_saved_revoke'

  return i18n.global.t(key, { count }, count)
}

// Número con los separadores del idioma activo ("1,043" en inglés).
export function formatNumber (value) {
  return value.toLocaleString(i18n.global.locale?.value || 'es')
}
